/**
 * Variable utilities for step-qualified variable references.
 *
 * @module variable-utils
 *
 * Provides slugification of step labels for use in qualified variable
 * references like `$var.Step2_Create_Client.client`.
 */

import { MACRO_TO_VAR_KEY, scanStringForMacros } from './macros.js';
import { resolveNodeOutputs } from './node-outputs.js';

/**
 * Slugify a seed workflow name for use in variable namespacing.
 *
 * Examples:
 *   slugifySeedName("Create Client & Matter") → "Create_Client_Matter"
 *   slugifySeedName("OAuth Credential Seed")  → "OAuth_Credential_Seed"
 *
 * Used by both runtime (seed-execution-dag.ts) and frontend (useWorkflowVariables)
 * to produce consistent qualified variable references like `$var.Create_Client_Matter.client_id`.
 */
export function slugifySeedName(name: string): string {
  return name
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Convert a step index + label into a slug suitable for qualified variable references.
 *
 * Examples:
 *   slugifyStepLabel(2, "Create Client") → "Step2_Create_Client"
 *   slugifyStepLabel(5, "Create Time Entry") → "Step5_Create_Time_Entry"
 *   slugifyStepLabel(1, "Login") → "Step1_Login"
 *
 * Always prefixed with step number for unambiguous references.
 * Non-alphanumeric characters are replaced with underscores, consecutive
 * underscores are collapsed, and leading/trailing underscores are trimmed.
 */
export function slugifyStepLabel(stepIndex: number, label: string): string {
  const slug = label
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return `Step${stepIndex}_${slug}`;
}

/**
 * Canonical form for a single workflow variable LEAF name.
 *
 * Variable names are produced and consumed by many independent paths — the web
 * UI output editor, the builder agent, the cache optimizer, the live agent's
 * `saveAs`, and raw API writes. Historically each path used its own casing
 * convention, so a recipe could capture `target_member_name` while the authored
 * node declared `targetMemberName` and a downstream step referenced
 * `$var.targetMemberName`. Resolution is exact-match, so the consumer silently
 * failed to resolve the producer's value (see the team-settings persistence
 * incident, June 2026).
 *
 * This collapses camelCase/PascalCase boundaries, spaces, hyphens, and other
 * separators to a single snake_case form, lower-cased. It is **idempotent**
 * (`canonicalizeVariableName(canonicalizeVariableName(x)) === canonicalizeVariableName(x)`),
 * so applying it at every boundary makes producer and consumer names converge.
 *
 * Examples:
 *   canonicalizeVariableName("targetMemberName") → "target_member_name"
 *   canonicalizeVariableName("target_member_name") → "target_member_name"
 *   canonicalizeVariableName("URLPath")           → "url_path"
 *
 * Only IDENTIFIER-shaped names (`^[A-Za-z][A-Za-z0-9_]*$`) are normalized — these
 * are exactly the names that can be written as a bare `$var.X` reference, which
 * is where camelCase↔snake_case drift occurs. Any other name is returned
 * UNCHANGED:
 *   - Freeform names with spaces (`"Contract Name"`, `"Item Being Acquired"`) are
 *     a supported legacy form used as literal registry keys — normalizing them
 *     would break literal resolution.
 *   - Dotted names — qualified (`Step2_Create_Client.client`), credential
 *     (`credential.username`), seed-namespaced (`Create_Client_Matter.client_id`)
 *     — carry a qualifier segment whose casing is significant.
 * Label→identifier conversion (spaces/hyphens → snake_case) is a separate concern
 * handled at authoring input (see the web output/input editors).
 */
export function canonicalizeVariableName(name: string): string {
  if (typeof name !== 'string') return name;
  if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) return name;
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // camelCase boundary: targetMember → target_Member
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2') // acronym boundary: URLPath → URL_Path
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();
}

/**
 * Extract variable names defined by a node's config.
 * Works on both frontend WorkflowNode.data and backend configJson.
 * Reusable by: frontend variableScanning, backend builder tools.
 *
 * Leaf names are returned in canonical snake_case form (see
 * {@link canonicalizeVariableName}) so producer/consumer matching is
 * casing-insensitive across the authoring paths.
 */
export function extractDefinedVariablesFromConfig(
  config: Record<string, unknown>,
  nodeType?: string
): string[] {
  const names: string[] = [];

  // dataRequirements names (the schema that makes inputs visible in the UI)
  const dataRequirements = config.dataRequirements as Array<{ name: string }> | undefined;
  const dataReqNames = new Set<string>();
  if (Array.isArray(dataRequirements)) {
    for (const req of dataRequirements) {
      if (typeof req.name === 'string') {
        const canonical = canonicalizeVariableName(req.name);
        names.push(canonical);
        dataReqNames.add(canonical);
      }
    }
  }

  // inputValues keys define variables, but ONLY when backed by a dataRequirement.
  // Orphaned inputValues (no matching dataRequirement) are ghost data from previous
  // agent runs and should not be exposed as variables.
  const inputValues = config.inputValues as Record<string, string> | undefined;
  if (inputValues && dataReqNames.size > 0) {
    for (const [fieldName, fieldValue] of Object.entries(inputValues)) {
      const canonicalField = canonicalizeVariableName(fieldName);
      if (!dataReqNames.has(canonicalField)) continue;
      // dataRequirements already added the name; just scan for macro-produced vars
      if (typeof fieldValue === 'string') {
        const macroNames = scanStringForMacros(fieldValue);
        for (const macroName of macroNames) {
          const macroSuffix = MACRO_TO_VAR_KEY[macroName];
          if (macroSuffix) {
            names.push(`${canonicalField}_${macroSuffix}`);
          }
        }
      }
    }
  }

  // API sequence exports
  if (nodeType === 'api_sequence') {
    const exports = config.exports as Array<{ variableName?: string }> | undefined;
    if (Array.isArray(exports)) {
      for (const exp of exports) {
        if (typeof exp.variableName === 'string' && exp.variableName) {
          names.push(canonicalizeVariableName(exp.variableName));
        }
      }
    }
  }

  // Variables captured at runtime via context extractors (saveAs)
  const capturedVariableKeys = config.capturedVariableKeys as string[] | undefined;
  if (Array.isArray(capturedVariableKeys)) {
    for (const key of capturedVariableKeys) {
      if (typeof key === 'string') names.push(canonicalizeVariableName(key));
    }
  }

  // User-declared output variable names
  const declaredOutputs = config.declaredOutputs as string[] | undefined;
  if (Array.isArray(declaredOutputs)) {
    for (const key of declaredOutputs) {
      if (typeof key === 'string') names.push(canonicalizeVariableName(key));
    }
  }

  // Per-node-type output declarations (e.g. notification.to/subject/body)
  for (const output of resolveNodeOutputs(nodeType, config)) {
    names.push(output.name);
  }

  return names;
}
